import React, { useEffect, useState } from "react";
import { Building2, Copy, Clock, Link2Off } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo, LogoMark } from "@/components/Logo";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira } from "@/lib/format";

export const REPAY_DISCLAIMER =
  "Debii never receives or holds your money. Payment is made directly to the creditor.";

function Wrap({ children }) {
  return (
    <div className="debii-shell min-h-screen px-6 py-8">
      <div className="flex justify-center"><Logo size={30} /></div>
      <div className="mt-8 max-w-sm mx-auto w-full animate-rise">{children}</div>
    </div>
  );
}

// Reusable no-account repayment flow. `basePath` points at the secure endpoints,
// e.g. `/a/${token}` (acknowledgement link) or `/d/${token}` (reminder/share link).
export default function PublicPay({ basePath, creditor, onClose }) {
  const [step, setStep] = useState("loading"); // loading | amount | details | confirm | done | error
  const [details, setDetails] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [reported, setReported] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`${basePath}/repayment-details`);
        setDetails(data);
        setAmount(String(data.outstanding_amount));
        setStep("amount");
      } catch {
        setStep("error");
      }
    })();
  }, [basePath]);

  const copyAcct = () => {
    navigator.clipboard?.writeText(details.account_number);
    toast.success("Account number copied");
  };

  const submitReport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${basePath}/report-repayment`, { amount: Number(amount) });
      setReported(data.amount);
      setStep("done");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cur = details?.currency || "NGN";
  const num = Number(amount);
  const valid = num > 0 && details && num <= details.outstanding_amount;
  const who = details?.creditor_name || creditor;

  return (
    <Wrap>
      {step === "loading" && <div className="flex justify-center py-16"><div className="animate-pop"><LogoMark size={48} /></div></div>}

      {step === "error" && (
        <div className="text-center py-10">
          <Link2Off className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">Repayment details aren't available for this record.</p>
          <Button variant="outline" onClick={onClose} className="mt-4 h-11 rounded-xl">Back</Button>
        </div>
      )}

      {step === "amount" && details && (
        <div>
          <h1 className="font-display text-xl font-bold text-foreground">Pay {who}</h1>
          <div className="mt-4 rounded-2xl bg-card border border-border p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Outstanding balance</p>
            <p className="font-display text-3xl font-extrabold tabular text-foreground">{fmtNaira(details.outstanding_amount, cur)}</p>
          </div>
          <p className="mt-4 text-sm font-medium text-foreground">How much are you paying?</p>
          <div className="mt-2 flex gap-2">
            <Button type="button" variant="outline" data-testid="ackpay-full-btn" onClick={() => setAmount(String(details.outstanding_amount))} className="h-11 rounded-xl flex-1">Pay full balance</Button>
          </div>
          <Input data-testid="ackpay-amount-input" type="number" inputMode="decimal" value={amount}
                 onChange={(e) => setAmount(e.target.value)} className="mt-2 h-12 rounded-xl text-lg" placeholder="Enter amount" />
          <Button data-testid="ackpay-continue-btn" disabled={!valid} onClick={() => setStep("details")} className="mt-4 w-full h-12 rounded-xl">Continue</Button>
          <Button variant="ghost" onClick={onClose} className="mt-1 w-full h-10 rounded-xl">Cancel</Button>
        </div>
      )}

      {step === "details" && details && (
        <div>
          <h1 className="font-display text-xl font-bold text-foreground">Pay {fmtNaira(num, cur)} to:</h1>
          <div className="mt-4 rounded-3xl bg-card border border-border p-5">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center"><Building2 className="h-6 w-6 text-primary" /></div>
              <div>
                <p className="font-display font-bold text-foreground">{details.account_name}</p>
                <p className="text-sm text-muted-foreground">{details.bank_name}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-xl bg-secondary px-4 py-3">
              <span data-testid="ackpay-acct-number" className="font-mono text-lg font-semibold tracking-wide text-foreground">{details.account_number}</span>
              <button data-testid="ackpay-copy-acct" onClick={copyAcct} className="flex items-center gap-1 text-sm font-semibold text-primary"><Copy className="h-4 w-4" /> Copy</button>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground text-center">{REPAY_DISCLAIMER}</p>
          <Button data-testid="ackpay-made-payment-btn" onClick={() => setStep("confirm")} className="mt-4 w-full h-12 rounded-xl">I've made the payment</Button>
          <Button variant="ghost" onClick={() => setStep("amount")} className="mt-1 w-full h-10 rounded-xl">Back</Button>
        </div>
      )}

      {step === "confirm" && details && (
        <div>
          <h1 className="font-display text-xl font-bold text-foreground">Did you pay {fmtNaira(num, cur)} to {who}?</h1>
          <div className="mt-4 rounded-2xl bg-card border border-border p-4">
            <div className="flex items-center justify-between py-2.5">
              <span className="text-sm text-muted-foreground">Account</span>
              <span className="text-sm font-semibold text-foreground text-right">{details.bank_name} {details.account_masked}</span>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">This tells {who} you've paid. They'll confirm before your balance updates.</p>
          <Button data-testid="ackpay-submit-btn" disabled={busy} onClick={submitReport} className="mt-4 w-full h-12 rounded-xl">{busy ? "Submitting…" : "Yes, submit payment"}</Button>
          <Button variant="ghost" onClick={() => setStep("details")} className="mt-1 w-full h-10 rounded-xl">Cancel</Button>
        </div>
      )}

      {step === "done" && (
        <div className="text-center py-6" data-testid="ackpay-done">
          <div className="mx-auto h-20 w-20 rounded-full bg-amber-100 flex items-center justify-center animate-pop"><Clock className="h-11 w-11 text-amber-700" /></div>
          <h1 className="mt-5 font-display text-2xl font-bold text-foreground">Payment reported</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your {fmtNaira(reported, cur)} payment has been sent to {creditor || who} for confirmation. Your balance will update once they confirm.
          </p>
          <Button onClick={onClose} className="mt-6 w-full h-12 rounded-xl">Done</Button>
        </div>
      )}
    </Wrap>
  );
}
